import { giftFrameCuriosityContext, type GiftFrameSource } from '@src/utils/giftFrameCuriosity';
import { trackGiftEvent } from '@src/utils/giftAnalytics';
import { AvatarFrameDecoration, type FrameDecoration } from './AvatarFrameDecoration';
import { avatarFrameFor } from './wizardAvatarFrame';
import {
  createElement,
  useContext,
  type ComponentType,
  type CSSProperties,
  type SVGProps,
} from 'react';
import { ThemeContext } from '@src/theme/ThemeContext';
import { AnimalBear } from '@src/components/faces/AnimalBear';
import { AnimalBobcat } from '@src/components/faces/AnimalBobcat';
import { AnimalCat } from '@src/components/faces/AnimalCat';
import { AnimalChimp } from '@src/components/faces/AnimalChimp';
import { AnimalChipmunk } from '@src/components/faces/AnimalChipmunk';
import { AnimalDeer } from '@src/components/faces/AnimalDeer';
import { AnimalElephant } from '@src/components/faces/AnimalElephant';
import { AnimalFox } from '@src/components/faces/AnimalFox';
import { AnimalGiraffe } from '@src/components/faces/AnimalGiraffe';
import { AnimalHyena } from '@src/components/faces/AnimalHyena';
import { AnimalHyppo } from '@src/components/faces/AnimalHyppo';
import { AnimalKoala } from '@src/components/faces/AnimalKoala';
import { AnimalLama } from '@src/components/faces/AnimalLama';
import { AnimalLamb } from '@src/components/faces/AnimalLamb';
import { AnimalLeopard } from '@src/components/faces/AnimalLeopard';
import { AnimalLion } from '@src/components/faces/AnimalLion';
import { AnimalOctopus } from '@src/components/faces/AnimalOctopus';
import { AnimalOwl } from '@src/components/faces/AnimalOwl';
import { AnimalPanda } from '@src/components/faces/AnimalPanda';
import { AnimalPenguin } from '@src/components/faces/AnimalPenguin';
import { AnimalQuestion } from '@src/components/faces/AnimalQuestion';
import { AnimalRabbit } from '@src/components/faces/AnimalRabbit';
import { AnimalRacoon } from '@src/components/faces/AnimalRacoon';
import { AnimalRoe } from '@src/components/faces/AnimalRoe';
import { AnimalScunk } from '@src/components/faces/AnimalScunk';
import { AnimalSloth } from '@src/components/faces/AnimalSloth';
import { AnimalSquirrel } from '@src/components/faces/AnimalSquirrel';
import { AnimalTiger } from '@src/components/faces/AnimalTiger';
import { AnimalUnknown } from '@src/components/faces/AnimalUnknown';
import { AnimalUnknown2 } from '@src/components/faces/AnimalUnknown2';
import { AnimalZebra } from '@src/components/faces/AnimalZebra';
import { classes } from '@src/components/layout/space';
import './FaceFrame.css';
import './ParticipantFace.css';

type Face = ComponentType<SVGProps<SVGSVGElement>>;

const FACES: Record<string, Face> = {
  bear: AnimalBear,
  bobcat: AnimalBobcat,
  cat: AnimalCat,
  chimp: AnimalChimp,
  chipmunk: AnimalChipmunk,
  deer: AnimalDeer,
  elephant: AnimalElephant,
  fox: AnimalFox,
  giraffe: AnimalGiraffe,
  hyena: AnimalHyena,
  hyppo: AnimalHyppo,
  koala: AnimalKoala,
  lama: AnimalLama,
  lamb: AnimalLamb,
  leopard: AnimalLeopard,
  lion: AnimalLion,
  octopus: AnimalOctopus,
  owl: AnimalOwl,
  panda: AnimalPanda,
  penguin: AnimalPenguin,
  question: AnimalQuestion,
  rabbit: AnimalRabbit,
  racoon: AnimalRacoon,
  roe: AnimalRoe,
  scunk: AnimalScunk,
  sloth: AnimalSloth,
  squirrel: AnimalSquirrel,
  tiger: AnimalTiger,
  unknown: AnimalUnknown,
  unknown2: AnimalUnknown2,
  zebra: AnimalZebra,
};

/*
 * Same stock and order as v1 DEFAULT_ANIMAL_TYPES: backend picture names,
 * sorted in English, used when a card has no picture yet.
 */
export const CARD_PICTURE_NAMES = Object.keys(FACES)
  .filter((name) => name !== 'question')
  .sort((left, right) => left.localeCompare(right, 'en'));

export type ParticipantFaceSize = 'row' | 'identity' | 'card' | 'round';

export interface ParticipantFaceIdentity {
  isYou?: boolean;
  picture?: string | null;
  avatar?: string | null;
  frameName?: string | null;
}

export interface ParticipantFaceProps extends ParticipantFaceIdentity {
  /** Card/profile identity. Explicit picture, avatar, or frameName props override its fields. */
  card?: ParticipantFaceIdentity | null;
  /** Position in the list the face belongs to. Ignored when `picture` is set. */
  index?: number;
  /**
   * row — beside a name in a list. identity — beside a name in an identity
   * card. card — the giftee on their own. round — a circle, for exchange cards.
   */
  size?: ParticipantFaceSize;
  /** Dimmed, for someone who has not opened their card yet. */
  muted?: boolean;
  className?: string;
  decoration?: FrameDecoration;
  curiositySource?: GiftFrameSource;
  isOwn?: boolean;
}

function faceNameFor(picture?: string | null, index = 0): string {
  if (picture && FACES[picture]) {
    return picture;
  }
  if (picture) {
    return 'unknown';
  }
  const name = CARD_PICTURE_NAMES[Math.abs(Math.trunc(index)) % CARD_PICTURE_NAMES.length];
  return name;
}

/** A participant's avatar: their stored animal, or a stand-in by position. */
export function ParticipantFace({
  index = 0,
  card,
  picture = card?.picture,
  avatar = card?.avatar,
  frameName = card?.frameName,
  size = 'row',
  muted = false,
  className,
  decoration = 'stars',
  curiositySource,
  isOwn = card?.isYou,
}: ParticipantFaceProps) {
  const theme = useContext(ThemeContext);
  const frame = avatarFrameFor(frameName);
  const curiosity = giftFrameCuriosityContext(frame?.name, curiositySource, isOwn);
  const name = faceNameFor(picture, index);
  const Face = FACES[name];
  const seasonalArtwork = theme?.seasonPackage.participantPictures?.[name];

  return (
    <span
      onClickCapture={curiosity ? () => trackGiftEvent('gift_frame_curious_click', curiosity) : undefined}
      style={
        {
          ...(frame
            ? {
                background: frame.backdrop,
                boxShadow: 'none',
                '--avatar-scale': frame.avatarScale ?? 0.64,
                '--frame-display-scale': frame.displayScale ?? 1,
              }
            : {}),
          '--animal-scale': name === 'bear' ? 0.9 : 1,
        } as CSSProperties
      }
      data-frame-name={frame?.name}
      className={classes(
        'participant-face',
        frame && 'participant-face--wizard',
        size !== 'row' && `participant-face--${size}`,
        muted && 'participant-face--muted',
        className,
      )}
    >
      {frame?.effects && <AvatarFrameDecoration mode={decoration} effects={frame.effects} />}
      {picture === 'custom_avatar' && avatar ? (
        <img src={avatar} alt="" />
      ) : seasonalArtwork ? (
        <img className="participant-face__theme-art" src={seasonalArtwork} alt="" />
      ) : (
        createElement(Face)
      )}
      {frame && (
        <img
          className="participant-face__wizard-frame"
          style={{
            pointerEvents: curiosity ? 'auto' : undefined,
            width: `${frame.size}%`,
            height: `${frame.size}%`,
            left: `${50 - (frame.centerX * frame.size) / 100}%`,
            top: `${50 - (frame.centerY * frame.size) / 100}%`,
          }}
          src={frame.src}
          alt=""
          aria-hidden="true"
        />
      )}
    </span>
  );
}
